import { useCallback, useState } from "react";
import { Link } from "react-router-dom";
import AppHeader from "@/components/feature/AppHeader";
import AppFooter from "@/components/feature/AppFooter";
import StaffList from "@/pages/admin/staff/components/StaffList";
import PermissionMatrix from "@/pages/admin/staff/components/PermissionMatrix";
import UserPermissionOverride from "@/pages/admin/staff/components/UserPermissionOverride";
import MemberChangeLog from "@/pages/admin/staff/components/MemberChangeLog";
import { useStaff } from "@/pages/admin/staff/hooks/useStaff";
import { useCurrentUser } from "@/hooks/useCurrentUser";

interface Notice {
  text: string;
  tone: "success" | "error";
}

export default function StaffPage() {
  const { members, permissions, userPermissions, loading, error, reload, setRole, savePermissions, saveUserPermissions, loadChanges } = useStaff();
  const { user } = useCurrentUser();
  const [notice, setNotice] = useState<Notice | null>(null);

  const notify = useCallback((text: string, tone: "success" | "error" = "success") => {
    setNotice({ text, tone });
    window.setTimeout(() => setNotice(null), 4000);
  }, []);

  const adminCount = members.filter((member) => member.role === "admin").length;
  const staffCount = members.filter((member) => member.role === "staff").length;

  const cards = [
    { label: "成员总数", value: members.length, icon: "ri-team-line", tone: "primary" },
    { label: "超管", value: adminCount, icon: "ri-shield-star-line", tone: "primary" },
    { label: "客服", value: staffCount, icon: "ri-customer-service-2-line", tone: "accent" },
  ];

  const toneClass: Record<string, string> = {
    primary: "bg-primary-100 text-primary-700",
    accent: "bg-accent-100 text-accent-700",
    secondary: "bg-secondary-100 text-secondary-900",
  };

  return (
    <div className="flex min-h-screen flex-col bg-background-50">
      <AppHeader />

      <main className="flex-1">
        <section className="border-b border-background-200 bg-background-100">
          <div className="mx-auto w-full max-w-6xl px-4 py-12 md:px-6 md:py-16">
            <nav className="flex items-center gap-1.5 text-xs text-foreground-500">
              <Link to="/" className="cursor-pointer transition-colors hover:text-foreground-950">
                首页
              </Link>
              <span className="flex h-3 w-3 items-center justify-center">
                <i className="ri-arrow-right-s-line" />
              </span>
              <Link to="/admin" className="cursor-pointer transition-colors hover:text-foreground-950">
                管理后台
              </Link>
              <span className="flex h-3 w-3 items-center justify-center">
                <i className="ri-arrow-right-s-line" />
              </span>
              <span className="text-foreground-800">管理员管理</span>
            </nav>
            <div className="mt-4 flex flex-col justify-between gap-4 md:flex-row md:items-end">
              <div>
                <span className="inline-flex items-center gap-2 rounded-full bg-primary-100 px-3 py-1 text-xs font-medium text-primary-700">
                  <span className="flex h-3 w-3 items-center justify-center">
                    <i className="ri-shield-user-line" />
                  </span>
                  成员与权限
                </span>
                <h1 className="mt-3 font-heading text-2xl font-semibold tracking-tight text-foreground-950 md:text-4xl">
                  管理员管理
                </h1>
                <p className="mt-3 max-w-2xl text-sm leading-relaxed text-foreground-600 md:text-base">
                  在这里授予或撤销管理员、把成员调整为客服，并通过权限矩阵控制客服能进入哪些后台模块。除角色级权限外，还可对个别客服设置「个人权限覆盖」单独放行或收回某个模块；所有角色与权限变更都会记录在「成员变更记录」中，方便审计追责。超管始终保留全部权限，系统会保证至少存在一名超管。
                </p>
              </div>
              <Link
                to="/admin"
                className="inline-flex w-full cursor-pointer items-center justify-center gap-1.5 whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-4 py-2.5 text-sm font-medium text-foreground-800 transition-colors hover:border-primary-300 hover:bg-background-100 md:w-auto"
              >
                <span className="flex h-4 w-4 items-center justify-center text-primary-700">
                  <i className="ri-arrow-left-line" />
                </span>
                返回管理后台
              </Link>
            </div>
          </div>
        </section>

        <section className="mx-auto w-full max-w-6xl px-4 py-10 md:px-6 md:py-14">
          {error ? (
            <div className="flex flex-col items-center gap-3 rounded-lg border border-background-200 bg-background-100 py-16 text-center">
              <span className="flex h-11 w-11 items-center justify-center rounded-md bg-accent-100 text-accent-700">
                <i className="ri-error-warning-line text-xl" />
              </span>
              <p className="text-sm text-foreground-600">{error}</p>
              <button
                type="button"
                onClick={reload}
                className="cursor-pointer rounded-md bg-primary-500 px-4 py-2 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
              >
                重新加载
              </button>
            </div>
          ) : (
            <>
              <div className="grid gap-4 sm:grid-cols-3">
                {cards.map((card) => (
                  <div key={card.label} className="rounded-lg border border-background-200 bg-background-50 p-4">
                    <div className="flex items-center justify-between">
                      <span className="text-xs text-foreground-600">{card.label}</span>
                      <span className={`flex h-8 w-8 items-center justify-center rounded-md ${toneClass[card.tone]}`}>
                        <i className={`${card.icon} text-base`} />
                      </span>
                    </div>
                    <p className="mt-2 font-heading text-2xl font-semibold text-foreground-950">
                      {loading ? "--" : card.value}
                    </p>
                  </div>
                ))}
              </div>

              <div className="mt-6 space-y-6">
                <StaffList
                  members={members}
                  loading={loading}
                  currentUserId={user?.id ?? null}
                  onSetRole={setRole}
                  notify={notify}
                />
                <PermissionMatrix
                  permissions={permissions}
                  loading={loading}
                  onSave={savePermissions}
                  notify={notify}
                />
                <UserPermissionOverride
                  members={members}
                  userPermissions={userPermissions}
                  loading={loading}
                  onSave={saveUserPermissions}
                  notify={notify}
                />
                <MemberChangeLog loadChanges={loadChanges} notify={notify} />
              </div>
            </>
          )}
        </section>
      </main>

      {notice && (
        <div className="fixed bottom-6 left-1/2 z-[60] -translate-x-1/2 px-4">
          <div
            className={`lh-fade-up flex items-center gap-2.5 rounded-lg px-4 py-3 text-sm shadow-sm ${
              notice.tone === "success" ? "bg-primary-600 text-background-50" : "bg-accent-600 text-background-50"
            }`}
          >
            <span className="flex h-4 w-4 items-center justify-center">
              <i className={notice.tone === "success" ? "ri-checkbox-circle-line" : "ri-error-warning-line"} />
            </span>
            {notice.text}
          </div>
        </div>
      )}

      <AppFooter />
    </div>
  );
}